import {
	Button, EmptyState, Grid,
	SmallDialog,
	Switch,
	Text,
	toast,
} from '@components';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ActiveCarrier, RateCard as RateCardState } from '@state';
import {
	BUTTON_SIZE, BUTTON_TYPES, EMPTY_STATE_IMAGE, EMPTY_STATE_TYPES, TRateCardModifierSet
} from '@types';
import { buildLaneAndValidate, getFuelLevyLabels, getLaneFuelLevyId, getSelectedFuelLevy } from '@utils';
import styles from './RateCardFuelLevyEditor.module.scss';

import { useGetShipmentTags, useUpdateLane } from '@api';
import { useAllRateCardFuelLevyModifiersState, useFeatureFlag, useIsInRoute, useRefetchLane } from '@hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

type TProps = {
	isDisabled?: boolean;
}

export const RateCardFuelLevyEditor = ({ isDisabled = false }: TProps) => {
	const [editFuelLevyModalOpen, setEditFuelLevyModalOpen] = useState(false);
	const [rateCardModifiers, setRateCardModifiers] = useAllRateCardFuelLevyModifiersState()

	const [rateCard] = useRecoilState(RateCardState);
	const [shipperTag, setShipperTag] = useState<string | null>(null);

	const carrierInRoute = useIsInRoute('carrier');
	const shipperInRoute = useIsInRoute('shipper');
	const isAdminRoute = !carrierInRoute && !shipperInRoute;
	const activeCarrier = useRecoilValue(ActiveCarrier);
	const customerFuelLevyFeatureFlag = useFeatureFlag('customer_fuel_levy');

	const {
		isLoadingUpdateLane,
		callUpdateLane,
	} = useUpdateLane();

	const { refetchLane } = useRefetchLane();

	const {
		tagsList,
		isLoadingTags,
		callGetShipmentTags,
	} = useGetShipmentTags();

	useEffect(() => {
		if (carrierInRoute && activeCarrier) {
			callGetShipmentTags();
		}
	}, [carrierInRoute, activeCarrier]);

	useEffect(() => {
		if (rateCard?.shipper_tag) {
			setShipperTag(rateCard?.shipper_tag.external_id);
		}
	}, [rateCard?.shipper_tag]);

	const handleFuelLevySwitch = useCallback((key: keyof TRateCardModifierSet, value: boolean) => {
		setRateCardModifiers({
			...rateCardModifiers,
			[key]: {
				...(rateCardModifiers as TRateCardModifierSet)[key as keyof TRateCardModifierSet],
				attracts_fuel_levy: value
			}
		} as any);
	}, [rateCardModifiers]);

	const handleSaveClick = () => {
		if (!rateCard) return;
		if (!rateCard.rate_cards.length) {
			toast.error('Please add at least one rate card type');
			return;
		}
		if (customerFuelLevyFeatureFlag && rateCard.fuel_levy_type === 'customer' && !getLaneFuelLevyId(rateCard)) {
			toast.error('Please select a fuel levy.');
			return;
		}
		if ((
			rateCard.fuel_levy_type === 'customer' &&
			!getSelectedFuelLevy(rateCard?.fuel_levies, 'customer', getLaneFuelLevyId(rateCard))?.current
		)
		|| (
			rateCard.fuel_levy_type === 'rate_card' && !rateCard?.fuel_levies?.rate_card?.current
		)) {
			toast.error('Please add fuel levy value.');

			return;
		}
		if (!rateCard?.external_id) {
			setEditFuelLevyModalOpen(false);
			return
		}

		const shipperTagObject = tagsList?.find((tag) => tag.external_id === shipperTag) ?? null;
		const { success, payload } = buildLaneAndValidate(
			{
				...rateCard,
				shipper_tag: shipperTagObject
			},
			isAdminRoute ? 'admin' : shipperInRoute ? 'shipper' : 'carrier'
		);

		if (success) {
			callUpdateLane({
				payload: { ...payload, expected_updated_at: rateCard.updated_at },
				routes: [rateCard?.external_id],
				returnFullError: true
			})
				.then(() => {
					// Get up-to-date data to display after save.
					if (rateCard.external_id !== null) {
						refetchLane(rateCard.external_id);
					}
					toast.success('Rate cards updated successfully');
					setTimeout(() => {
						setEditFuelLevyModalOpen(false);
					}, 500);
				})
				.catch((err) => {
					if (err?.cause?.response?.status === 409) {
						toast.error(
							err.cause.response.data?.message ?? 'This rate card was changed by someone else while you were editing. Reload the page to get the latest rates, then reapply your changes.',
							{ title: 'Rate card out of date' }
						);
						return;
					}
					toast.error(err?.cause?.response?.data?.errors ?? 'There has been an error trying to save the rate card');
				})
		} else {
			toast.error('Please review the errors and try again.')
		}
	}

	const slot = useMemo(() => {
		if (Object.keys(rateCardModifiers || {}).length === 0) {
			return <div className={styles.emptyContainer}>
				<EmptyState title='There are no modifiers defined on this rate card yet' type={EMPTY_STATE_TYPES.FIRST_USE} image={EMPTY_STATE_IMAGE.TUMBLEWEED} />
			</div>;
		}

		return (
			<div className={styles.modalContent}>
				{Object.entries(rateCardModifiers || {}).map(
					([key, value]) => {
						return <Grid key={key} columns={2} columnTemplate={'auto 48px'} rowHeight='36px'>
							<Grid.Item>
								<Text>{getFuelLevyLabels(key)}</Text>
							</Grid.Item>
							<Grid.Item>
								<Switch name={key} checked={!!(value as any)?.attracts_fuel_levy} onChange={(e) => { handleFuelLevySwitch(key as keyof TRateCardModifierSet, e.target.checked) } } value={''} />
							</Grid.Item>
						</Grid>
					})}
			</div>
		)
	}, [rateCardModifiers]);

	return (
		<>
			<Button
				type={BUTTON_TYPES.TERTIARY}
				Icon={EditOutlinedIcon}
				text={'Modifier Fuel Levy'}
				onClick={() => setEditFuelLevyModalOpen(true)}
				disabled={isDisabled}
			/>
			<SmallDialog
				open={editFuelLevyModalOpen}
				title={'Add fuel levy to Modifiers'}
				text={'By default, fuel levy is only applied to the base charge. If fuel levy is also required on a specific Modifier, toggle it on.'}
				slot={slot}
				actionButtonText='Save'
				actionButtonSize={BUTTON_SIZE.SMALL}
				actionButtonLoading={isLoadingUpdateLane || isLoadingTags}
				actionButtonDisabled={Object.keys(rateCardModifiers || {}).length === 0}
				actionButtonFn={handleSaveClick}
				cancelButtonText='Cancel'
				onClose={() => setEditFuelLevyModalOpen(false)}
			/>
		</>
	);
}
